import React, { useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { TRAITS, hasTrait } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { useToggleTop } from "../../../components/common/ToggleTop";
import {
  DEFAULT_ERROR,
  NumberField,
  SettingsPage,
  SettingsSkeleton,
  SliderField,
  UPDATED,
  Unavailable,
  UpdateButton,
  clamp,
  goBack,
  inRange,
  readConfigDevice,
  settingsPayload,
  toText,
  useAlive,
} from "./shared";
import "../../../assets/styles/app-settings.css";

const FORM_ID = "hx-set-power-form";
const fieldId = (key) => `hx-set-power-${key}`;

// Trip voltages and overload cutoff from the device's first settings entry, or
// null when it has none.
const readPower = (source) => {
  const settings = source?.deviceSettings?.[0]?.settings;
  if (!settings) return null;
  return {
    highVoltageTrip: toText(settings.highVoltageTrip),
    lowVoltageTrip: toText(settings.lowVoltageTrip),
    overLoadAmps: Number(settings.overLoadAmps),
    overLoadOffTime: toText(settings.overLoadOffTime),
  };
};

// Checked in the app's order (PowerSettings.js checkSettingValid). The app
// reports a bad amps value as "Dry Run Off Time"; this names the right field.
const CHECKS = [
  { key: "highVoltageTrip", min: 220, max: 280, message: "High Voltage Trip Not in Range..!" },
  { key: "lowVoltageTrip", min: 170, max: 210, message: "Low Voltage Trip Not in Range..!" },
  { key: "overLoadOffTime", min: 1, max: 60, message: "Over Load Off Time Not in Range..!" },
  { key: "overLoadAmps", min: 1, max: 15, message: "Over Load Current Not in Range..!" },
];

// Power protection for devices with trait 107 (app/screens/DeviceSettings/
// PowerSettings.js), opened from the device's Settings page.
function PowerSettings() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = state?.data;
  const supported = hasTrait(device, TRAITS.POWER_SETTINGS);
  // undefined while reading, null when the device has no power settings.
  const [values, setValues] = useState();
  const [busy, setBusy] = useState(null);
  const [invalid, setInvalid] = useState(null);

  useEffect(() => {
    if (!device || !supported) return undefined;
    let current = true;
    // The app finds the device by its settings entry's applianceId.
    readConfigDevice(device.deviceSettings?.[0]?.applianceId ?? device.applianceId).then((fresh) => {
      if (current) setValues(readPower(fresh) ?? readPower(device));
    });
    return () => {
      current = false;
    };
  }, [device, supported]);

  // Takes the user to the value that failed the check.
  useEffect(() => {
    if (invalid) document.getElementById(fieldId(invalid.key))?.focus();
  }, [invalid]);

  if (!device) return <Redirect to="/dashboard" />;

  const change = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (invalid?.key === key) setInvalid(null);
  };

  const update = async (event) => {
    event.preventDefault();
    if (busy || !values) return;
    const problem = CHECKS.find((check) => !inRange(values[check.key], check.min, check.max));
    if (problem) {
      toast(problem.message, "red");
      setInvalid({ key: problem.key });
      return;
    }
    setBusy("Updating settings…");
    try {
      // Trait 106 with no id, as the app sends it.
      await sendControl(
        settingsPayload(device, {
          trait: Number(TRAITS.POWER_PROTECTION),
          value: {
            highVoltageTrip: Number(values.highVoltageTrip),
            lowVoltageTrip: Number(values.lowVoltageTrip),
            overLoadAmps: Number(values.overLoadAmps),
            overLoadOffTime: Number(values.overLoadOffTime),
          },
        })
      );
      toast(UPDATED, "green");
      if (!alive.current) return;
      setBusy(null);
      goBack(history);
    } catch (error) {
      if (alive.current) setBusy(null);
      toast(DEFAULT_ERROR, "red");
    }
  };

  const amps = values?.overLoadAmps;
  let body;
  if (!supported || values === null) {
    body = <Unavailable />;
  } else if (values === undefined) {
    body = <SettingsSkeleton tall />;
  } else {
    body = (
      <form id={FORM_ID} className="hx-set-card hx-set-form" noValidate onSubmit={update}>
        <NumberField
          id={fieldId("highVoltageTrip")}
          label="High voltage trip"
          value={values.highVoltageTrip}
          placeholder="220-280 Volts"
          hint="Range from 220 to 280 Volts"
          invalid={invalid?.key === "highVoltageTrip"}
          onChange={(value) => change("highVoltageTrip", value)}
        />
        <NumberField
          id={fieldId("lowVoltageTrip")}
          label="Low voltage trip"
          value={values.lowVoltageTrip}
          placeholder="170-210 Volts"
          hint="Range from 170 to 210 Volts"
          invalid={invalid?.key === "lowVoltageTrip"}
          onChange={(value) => change("lowVoltageTrip", value)}
        />
        <SliderField
          id={fieldId("overLoadAmps")}
          label="OverLoad Current"
          min={1}
          max={15}
          step={1}
          value={clamp(amps, 1, 15)}
          display={Number.isFinite(amps) ? String(amps) : "–"}
          unit="Amps"
          hint="Range from 1 to 15 Amps"
          invalid={invalid?.key === "overLoadAmps"}
          onChange={(value) => change("overLoadAmps", value)}
        />
        <NumberField
          id={fieldId("overLoadOffTime")}
          label="OverLoad Cutoff Delay"
          value={values.overLoadOffTime}
          placeholder="1-60 sec"
          hint="Range from 1 to 60 Seconds"
          invalid={invalid?.key === "overLoadOffTime"}
          onChange={(value) => change("overLoadOffTime", value)}
        />
      </form>
    );
  }

  return (
    <SettingsPage
      title="Power Settings"
      device={device}
      busy={busy}
      headerRight={<UpdateButton form={FORM_ID} busy={Boolean(busy)} disabled={!supported || !values} />}
    >
      {body}
    </SettingsPage>
  );
}

export default PowerSettings;
